import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {Card,Button,Modal,Form,InputGroup,Image,Container,Spinner,} from "react-bootstrap";
import {Camera,X,User,Search,Clock,ThumbsUp,MessageSquare,ThumbsDown,} from "lucide-react";
import moment from "moment";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart } from "react-feather";

const Feed = () => {
  const { id } = useParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const token = localStorage.getItem("jwttoken");
  const [likedPosts, setLikedPosts] = useState({});
  const [comments, setComments] = useState({});
  const [newComments, setNewComments] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [postContent, setPostContent] = useState("");
  const [attachedImage, setAttachedImage] = useState(null);
  const backendURL = "https://kaasbackend.vetrinahealthcare.com"; // Adjust based on your backend
  const [searchFeed, setSearchFeed] = useState(""); // For searching posts
  const [showComments, setShowComments] = useState({}); // For tracking which post's comments are open
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [id]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${backendURL}/socialapp/groups/${id}/posts/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setPosts(response.data);
      const userId = JSON.parse(atob(token.split(".")[1])).user_id;
      setLikedPosts(
        response.data.reduce((acc, post) => {
          acc[post.id] = post.likers.includes(userId);
          return acc;
        }, {})
      );
      setLoading(false);

      // Fetch comments for each post automatically
    } catch (error) {
      setErrors({ fetch: "Failed to load posts" });
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    if (comments[postId]) return; // Avoid fetching multiple times
    try {
      const response = await axios.get(
        `${backendURL}/socialapp/groups/${id}/posts/${postId}/comments/`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setComments((prev) => ({ ...prev, [postId]: response.data }));
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  const handleLikeToggle = async (postId, liked) => {
    setLikedPosts((prev) => ({ ...prev, [postId]: !liked }));
    setPosts((prev) =>
      prev.map((post) =>
        post.id === postId ? { ...post, likes_count: liked ? post.likes_count - 1 : post.likes_count + 1 } : post
      )
    );
  
    try {
      await axios.post(`${backendURL}/socialapp/groups/${id}/posts/${postId}/${liked ? "unlike" : "like"}/`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.error("Error toggling like:", error);
      setLikedPosts((prev) => ({ ...prev, [postId]: liked }));
      setPosts((prev) =>
        prev.map((post) =>
          post.id === postId ? { ...post, likes_count: liked ? post.likes_count + 1 : post.likes_count - 1 } : post
        )
      );
    }
  };
  
  const handleCommentChange = (postId, value) => {
    setNewComments((prev) => ({ ...prev, [postId]: value }));
  };

  const handlePostComment = async (postId) => {
    if (!newComments[postId]) return;

    try {
      await axios.post(
        `${backendURL}/socialapp/groups/${id}/posts/${postId}/comments/`,
        {
          content: newComments[postId],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setNewComments((prev) => ({ ...prev, [postId]: "" }));
      fetchComments(postId);
    } catch (error) {
      console.error("Error posting comment:", error);
    }
  };

  const handleImageUpload = (e) => {
    if (e.target.files[0]) {
      setAttachedImage(e.target.files[0]); // Store actual file instead of URL
    }
  };

  const handlePostSubmit = async () => {
    if (!postContent.trim()) {
      Swal.fire("Warning!", "Post content cannot be empty.", "warning");
      return;
    }
    setPosting(true);

    const formData = new FormData();
    formData.append("content", postContent);
    if (attachedImage) {
      // const response = await fetch(attachedImage);
      // const blob = await response.blob();
      formData.append("image", attachedImage);
    }

    try {
      await axios.post(
        `${backendURL}/socialapp/groups/${id}/posts/`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setPostContent("");
      setAttachedImage(null);
      setShowModal(false);
      fetchPosts();
    } catch (error) {
      console.error("Error adding post:", error);
    } finally {
      setPosting(false);
    }
  };

  const handleSearch = (query) => {
    if (!query.trim()) {
      fetchPosts(); // Reset feed if search is empty
      return;
    }
  
    const filteredPosts = posts.filter((post) =>
      post.content.toLowerCase().includes(query.toLowerCase())
    );
  
    setPosts(filteredPosts);
  };

  return (
    <Container style={{ marginTop: "20px", padding: "0 15px" }}>
      {/* Create Post Card */}
      <Card className="mb-3 shadow-sm border-0 rounded">
        <Card.Body
          className="d-flex align-items-center bg-white rounded p-3"
          style={{
            cursor: "pointer",
            transition: "0.3s",
            border: "1px solid #ddd",
            boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
          }}
          onClick={() => setShowModal(true)}
        >
          <User size={40} className="text-primary me-3" />
          <Form.Control
            placeholder="What's on your mind?"
            readOnly
            className="rounded-pill bg-light border px-3 shadow-sm"
          />
        </Card.Body>
      </Card>

      {/* Search Bar */}
      <InputGroup className="mb-4">
        <InputGroup.Text className="bg-white border-0 shadow-sm">
          <Search size={18} />
        </InputGroup.Text>
        <Form.Control
          type="text"
          placeholder="Search in feed..."
          value={searchFeed}
          onChange={(e) => {
            setSearchFeed(e.target.value);
            handleSearch(e.target.value);
          }}
          className="border-0 shadow-sm bg-light rounded-pill px-3"
        />
      </InputGroup>

      {/* Create Post Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Create a post</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="postContent">
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="What's on your mind?"
                value={postContent}
                onChange={(e) => setPostContent(e.target.value)}
                className="mb-3 border rounded shadow-sm"
              />
            </Form.Group>

            {attachedImage && (
              <div className="text-center position-relative mb-3">
                <Image
                  src={attachedImage}
                  alt="Preview"
                  className="rounded shadow-sm w-100"
                />
                <Button
                  variant="danger"
                  size="sm"
                  className="position-absolute top-0 end-0 mt-2 me-2"
                  onClick={() => setAttachedImage(null)}
                >
                  ✕
                </Button>
              </div>
            )}

            {/* Attach Image */}
            <Form.Group className="d-flex align-items-center">
              <label
                className="d-flex align-items-center border px-3 py-2 rounded text-primary shadow-sm"
                style={{ cursor: "pointer" }}
              >
                <Camera size={20} className="me-2" /> Attach Photo
                <Form.Control
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </label>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePostSubmit}
            disabled={posting}
          >
            {posting ? <Spinner size="sm" animation="border" /> : "Post"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Feed Loader */}
      {loading ? (
        <div className="text-center text-muted py-3">
          <Spinner animation="border" size="sm" className="me-2" /> Loading
          posts...
        </div>
      ) : (
        posts.map((post) => (
          <Card key={post.id} className="mb-4 shadow-sm rounded border-0">
            <Card.Body>
              {/* Post Header */}
              <div className="d-flex align-items-center">
                <Image
                  src={
                    post.user.profile_pic
                      ? `${backendURL}${post.user.profile_pic}`
                      : "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                  }
                  roundedCircle
                  style={{
                    width: "45px",
                    height: "45px",
                    marginRight: "12px",
                    objectFit: "cover",
                  }}
                  alt="User Profile"
                />
                <div>
                  <strong>
                    {post.user.first_name} {post.user.last_name}
                  </strong>
                  <p className="text-muted small m-0">
                    <Clock size={14} className="me-1" />
                    {moment(post.created_at).format("MMMM Do YYYY, h:mm A")}
                  </p>
                </div>
              </div>

              {/* Post Content */}
              <p
                className="mt-3 text-dark"
                style={{ fontSize: "1rem", lineHeight: "1.5" }}
              >
                {post.content}
              </p>

              {/* Post Image */}
              {post.image && (
                <motion.div
                  initial={{ opacity: 0.5 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5 }}
                  className="text-center"
                >
                  <Image
                    src={`${backendURL}/lms/media/group_posts/${post.image
                      .split("/")
                      .pop()}`}
                    className="rounded w-100 shadow-sm"
                    style={{ maxHeight: "400px", objectFit: "cover" }}
                  />
                </motion.div>
              )}

              {/* Like & Comment Buttons */}
              <div className="d-flex align-items-center gap-3 mt-3">
                <motion.div
                  whileTap={{ scale: 0.9 }}
                  onClick={() => handleLikeToggle(post.id, likedPosts[post.id])}
                  className="d-flex align-items-center gap-1 text-danger"
                  style={{ cursor: "pointer" }}
                >
                  <Heart
                    fill={likedPosts[post.id] ? "red" : "none"}
                    size={20}
                  />
                  <span>{post.likes_count} Likes</span>
                </motion.div>

                <div
                  onClick={() => {
                    setShowComments((prev) => ({
                      ...prev,
                      [post.id]: !prev[post.id],
                    }));
                    if (!comments[post.id]) fetchComments(post.id);
                  }}
                  className="d-flex align-items-center gap-1 text-primary"
                  style={{ cursor: "pointer" }}
                >
                  <MessageSquare size={20} />
                  <span>Comment</span>
                </div>
              </div>

              {/* Comments Section */}
              {showComments[post.id] && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-light p-3 rounded mt-3 shadow-sm"
                >
                  {/* Display Comments */}
                  {comments[post.id] && comments[post.id].length > 0 ? (
                    comments[post.id].map((comment) => (
                      <div
                        key={comment.id}
                        className="d-flex align-items-start mb-2"
                      >
                        <Image
                          src={
                            comment.user.profile_pic
                              ? `${backendURL}${comment.user.profile_pic}`
                              : "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                          }
                          roundedCircle
                          style={{
                            width: "35px",
                            height: "35px",
                            marginRight: "10px",
                          }}
                          alt="Profile Pic"
                        />
                        <div className="bg-white p-2 rounded shadow-sm">
                          <strong>
                            {comment.user.first_name} {comment.user.last_name}
                          </strong>
                          <p className="m-0">{comment.content}</p>
                          <small className="text-muted">
                            {moment(comment.created_at).fromNow()}
                          </small>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No comments yet.</p>
                  )}

                  {/* Input for new comment */}
                  <Form.Control
                    type="text"
                    placeholder="Write a comment..."
                    value={newComments[post.id] || ""}
                    onChange={(e) =>
                      handleCommentChange(post.id, e.target.value)
                    }
                    className="rounded-pill border-0 px-3"
                  />
                  <Button
                    size="sm"
                    variant="primary"
                    className="mt-2"
                    onClick={() => handlePostComment(post.id)}
                  >
                    Post
                  </Button>
                </motion.div>
              )}
            </Card.Body>
          </Card>
        ))
      )}
    </Container>
  );
};

export default Feed;
